<template>
  <!--
    Digits only. The model is the bare number as a string ("1500000"); the dots are display, put
    in while typing so a figure with six zeros can be read at a glance, and never sent.
    A dot, a letter or a second separator cannot be typed or pasted — kip has no decimals.
  -->
  <input
    ref="input"
    v-bind="$attrs"
    type="text"
    inputmode="numeric"
    autocomplete="off"
    :value="display"
    @keypress="onKeypress"
    @input="onInput"
  >
</template>

<script>
/** "450000.00" (as the API stores it) or "1.500.000" (as typed) → "450000" / "1500000". */
export function toDigits(value) {
  if (value === null || value === undefined) return '';

  // A number from the API carries a fractional part ("450000.00"); drop it rather than gluing it
  // on, which would turn 450000.00 into 45000000.
  const raw = typeof value === 'number' ? String(Math.trunc(value)) : String(value);
  const whole = /^\d+\.\d{1,2}$/.test(raw) ? raw.split('.')[0] : raw;

  return whole.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
}

/** "1500000" → "1.500.000" */
export function withDots(digits) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export default {
  name: 'ClaimMoneyInput',

  inheritAttrs: false,

  props: {
    value: { type: [String, Number], default: '' },
  },

  computed: {
    display() {
      return withDots(toDigits(this.value));
    },
  },

  watch: {
    // A seeded "450000.00" is normalised once, so validation and the payload see the same digits
    // the officer sees.
    value: {
      immediate: true,
      handler(value) {
        const digits = toDigits(value);
        if (value !== '' && value !== null && value !== undefined && digits !== String(value)) {
          this.$emit('input', digits);
        }
      },
    },
  },

  methods: {
    onKeypress(event) {
      if (event.ctrlKey || event.metaKey || event.key.length !== 1) return;
      if (!/\d/.test(event.key)) event.preventDefault();
    },

    onInput(event) {
      const el = event.target;
      const caret = el.selectionStart || 0;
      // How many digits sit left of the caret — the dots move, the digits do not.
      const digitsBefore = el.value.slice(0, caret).replace(/\D/g, '').length;

      const digits = toDigits(el.value.replace(/\D/g, ''));
      const shown = withDots(digits);

      // Written back by hand: when a rejected key leaves the model unchanged, Vue would not
      // re-render and the stray character would stay in the box.
      el.value = shown;

      let pos = 0;
      for (let seen = 0; pos < shown.length && seen < digitsBefore; pos++) {
        if (/\d/.test(shown[pos])) seen++;
      }
      el.setSelectionRange(pos, pos);

      this.$emit('input', digits);
    },
  },
};
</script>
